import React, { useState } from 'react';
import { Calendar, Users, Sparkles, Video, Clock, Music, Utensils, Wine, Coffee, IceCream, Mic2, Lightbulb, MonitorPlay, Volume2, Building, Plus, Minus, Info, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';

const GreinerOfferPage = () => {
  // Global state for pricing calculations
  const [guestCount, setGuestCount] = useState(380);
  const [serviceType, setServiceType] = useState('eco');
  const [selectedHeadliner, setSelectedHeadliner] = useState(null);
  const [selectedSupportBand, setSelectedSupportBand] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState('vsetin'); // 'vsetin', 'frystak', or 'lukov'

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Hero Section */}
      <HeroSection />
      
      {/* Intro Section */}
      <IntroSection 
        selectedVenue={selectedVenue}
        setSelectedVenue={setSelectedVenue}
      />
      
      {/* Catering Section */}
      <CateringSection 
        guestCount={guestCount}
        setGuestCount={setGuestCount}
        serviceType={serviceType}
        setServiceType={setServiceType}
      />
      
      {/* Band Selection Section */}
      <BandSelectionSection 
        selectedHeadliner={selectedHeadliner}
        setSelectedHeadliner={setSelectedHeadliner}
        selectedSupportBand={selectedSupportBand}
        setSelectedSupportBand={setSelectedSupportBand}
      />
      
      {/* Program Timeline */}
      <ProgramSection 
        selectedHeadliner={selectedHeadliner}
        selectedSupportBand={selectedSupportBand}
      />
      
      {/* Technical Production */}
      <TechSection />
      
      {/* Grand Total Section */}
      <GrandTotalSection 
        guestCount={guestCount}
        serviceType={serviceType}
        selectedHeadliner={selectedHeadliner}
        selectedSupportBand={selectedSupportBand}
        selectedVenue={selectedVenue}
      />
      
      {/* Contact Footer */}
      <ContactSection />
    </div>
  );
};

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/greiner-hero-2.jpg"
          alt="Elegant Banquet Hall Setup with Golden Decorations"
          className="w-full h-full object-cover"
        />
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(2,6,23,0) 0%, rgba(2,6,23,0.8) 100%)'
          }}
        />
      </div>

      {/* Noise Texture */}
      <div 
        className="absolute inset-0 opacity-[0.05] pointer-events-none z-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24 text-center">
        {/* Logo */}
        <div className="mb-12 flex justify-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
            <img 
              src="/greiner-logo.svg"
              alt="Greiner Assistec"
              className="h-12 md:h-16 w-auto filter drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Main Title */}
        <h1 
          className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium leading-[0.9] text-white mb-6 tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Vánoční večírek 2026
        </h1>

        {/* Subtitle */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <span 
            className="text-xl md:text-2xl font-semibold"
            style={{ 
              fontFamily: "'Manrope', sans-serif",
              background: 'linear-gradient(135deg, #d4af37 0%, #fcd34d 50%, #d4af37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Greiner Assistec
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 mb-12">
          {/* Stat 1: Guests */}
          <div className="relative group">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-2">
              <Users className="w-8 h-8 text-emerald-400 mb-3 mx-auto" />
              <div 
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                380
              </div>
              <div className="text-slate-400 text-sm font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Hostů
              </div>
            </div>
          </div>

          {/* Stat 2: Budget */}
          <div className="relative group">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-2">
              <Sparkles className="w-8 h-8 text-emerald-400 mb-3 mx-auto" />
              <div 
                className="text-3xl md:text-4xl font-bold text-white mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                1.1-1.3M
              </div>
              <div className="text-slate-400 text-sm font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Rozpočet (Kč)
              </div>
            </div>
          </div>

          {/* Stat 3: Duration */}
          <div className="relative group">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-2">
              <Calendar className="w-8 h-8 text-emerald-400 mb-3 mx-auto" />
              <div 
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                6h
              </div>
              <div className="text-slate-400 text-sm font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Délka programu
              </div>
            </div>
          </div>

          {/* Stat 4: Service */}
          <div className="relative group">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-2">
              <Sparkles className="w-8 h-8 text-emerald-400 mb-3 mx-auto" />
              <div 
                className="text-xl md:text-2xl font-bold text-white mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Full service
              </div>
              <div className="text-slate-400 text-sm font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
                zajištění
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Intro Section - The Concept
const IntroSection = ({ selectedVenue, setSelectedVenue }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  const venues = {
    vsetin: {
      name: 'Kulturní dům Vsetín',
      address: 'Svárov 1055, 755 01 Vsetín 1',
      date: '4.12.2026',
      price: 91380,
      image: '/kd-vsetin-stage.jpg',
      capacity: [
        'Hlavní sál kapacita 364 sedících osob + taneční parket',
        'Při požadavku 380 sedících osob otevření balkonu (kapacita 91 míst)',
        'Velké foyer - rozdělení cateringu na sekce pro eliminaci fronty',
        'Malý sál zůstává uzavřen'
      ],
      advantages: [
        'Velké dominantní pódium o rozměru 16×8m (jedno z největších na Moravě)',
        'Prostorné foyer pro registraci příchozích hostů, realizaci rautu a fotokoutku',
        'Pohodlná kapacita šatny a WC',
        'Security a šatnářky poskytuje KD Vsetín v ceně nájmu (úspora 11 730 Kč)'
      ],
      disadvantages: [],
      pdfLayout: '/layout-mist.pdf',
      virtualTour: 'https://dkvsetin.pano3d.eu/',
      gallery: [
        '/kd-vsetin-sal-prazdny.jpg',
        '/kd-vsetin-foyer-1.jpg',
        '/kd-vsetin-foyer-2.jpg',
        '/kd-vsetin-koncert-1.jpg',
        '/kd-vsetin-koncert-2.png',
        '/kd-vsetin-koncert-3.jpg'
      ]
    },
    frystak: {
      name: 'Kulturní dům Fryšták',
      address: 'Holešovská 166, 763 16 Fryšták',
      date: '20.11.2026',
      price: 116471,
      image: '/kd-frystak-stage.jpg',
      capacity: [
        'Kapacita sedících v hlavním sále při zachování tanečního parketu: 380 osob',
        'Kapacita sedících na balkoně: 302 osob',
        'Foyer: 53 m²',
        'Šatna'
      ],
      advantages: [
        'Vzdálenost 10 km od firmy'
      ],
      disadvantages: [
        'Výrazně měnší pódium (špatná viditelnost led obrazovky i vystupujících, pódium je zapuštěné do hloubky)',
        'Méně reprezentativní',
        'Malé foyer (problematické zejména pro realizaci rautu a doplňkových aktivit)'
      ],
      pdfLayout: null,
      conditionsDocument: '/frystak-podminky.png',
      gallery: [
        '/kd-frystak-foyer.jpg',
        '/kd-frystak-sal.jpg',
        '/kd-frystak-stage.jpg',
        '/kd-frystak-balkon.jpg',
        '/kd-frystak-vchod.jpg'
      ]
    },
    lukov: {
      name: 'KD LUKOV',
      address: 'Hradská I 309, 763 17 Lukov',
      date: '4.12.2026',
      price: 25000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/rybs05mn_252940e7-73b2-48f6-839d-2979c90d34d5.JPG',
      capacity: [
        'Hlavní sál 30×17m + prostorný balkón',
        'Předsálí kapacita cca 80 osob',
        'Do 400 osob dostačující prostor'
      ],
      advantages: [
        'Nejlepší cena',
        'V blízkosti firmy',
        'Zázemí srovnatelné s KD Fryšták'
      ],
      disadvantages: [],
      pdfLayout: null,
      virtualTour: null,
      conditionsDocument: null,
      gallery: [
        'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/2xccxa58_2aafbc7a-e3f8-475f-9ab7-88f4394096a0.JPG',
        'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/jfu1e3di_29ae31ee-1f8f-4797-8b5f-39c13526cb75.JPG',
        'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/3pcj2qqu_50f707e2-e702-42ef-90db-b3e885d0ed71.JPG',
        'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/clwm7qpl_3031a564-6c7a-40a3-97bd-0e92cec01408.JPG',
        'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/rybs05mn_252940e7-73b2-48f6-839d-2979c90d34d5.JPG'
      ]
    }
  };

  const currentVenue = venues[selectedVenue];

  return (
    <section className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <h2 
          className="font-serif text-4xl md:text-5xl text-slate-100 mb-12 tracking-tight text-center"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Vyberte místo konání
        </h2>

        {/* Venue Selection Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setSelectedVenue('vsetin')}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
              selectedVenue === 'vsetin'
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            KD Vsetín
            <span className="block text-sm mt-1">91 380 Kč</span>
          </button>
          <button
            onClick={() => setSelectedVenue('frystak')}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
              selectedVenue === 'frystak'
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            KD Fryšták
            <span className="block text-sm mt-1">116 471 Kč</span>
          </button>
          <button
            onClick={() => setSelectedVenue('lukov')}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
              selectedVenue === 'lukov'
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            KD LUKOV
            <span className="block text-sm mt-1">25 000 Kč</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div>
            {/* Venue Details */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-6">
              <h3 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {currentVenue.name}
              </h3>
              <div className="space-y-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <p><strong className="text-emerald-400">Adresa:</strong> {currentVenue.address}</p>
                <p><strong className="text-emerald-400">Datum rezervace:</strong> {currentVenue.date}</p>
                <p><strong className="text-emerald-400">Cena pronájmu:</strong> {currentVenue.price.toLocaleString('cs-CZ')} Kč{selectedVenue === 'frystak' ? ' (10:00 až 3:00)' : ''}</p>
                {selectedVenue === 'frystak' && (
                  <p className="text-sm text-slate-400 italic">Cena 6 851 Kč za celý objekt na hodinu vč. příprav</p>
                )}
              </div>
            </div>

            {/* Capacity Info */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-6">
              <h4 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Kapacita a prostory
              </h4>
              <ul className="space-y-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {currentVenue.capacity.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Advantages */}
            {currentVenue.advantages.length > 0 && (
              <div className="bg-emerald-900/20 backdrop-blur-xl border border-emerald-400/30 rounded-2xl p-6 mb-6">
                <h4 className="text-lg font-semibold text-emerald-300 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  ✓ Výhody {currentVenue.name}
                </h4>
                <ul className="space-y-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {currentVenue.advantages.map((item, index) => (
                    <li key={index}>+ {item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disadvantages */}
            {currentVenue.disadvantages.length > 0 && (
              <div className="bg-amber-900/20 backdrop-blur-xl border border-amber-400/30 rounded-2xl p-6 mb-6">
                <h4 className="text-lg font-semibold text-amber-300 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  ⚠ Nevýhody {currentVenue.name}
                </h4>
                <ul className="space-y-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {currentVenue.disadvantages.map((item, index) => (
                    <li key={index}>- {item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            {(currentVenue.pdfLayout || currentVenue.virtualTour || currentVenue.conditionsDocument || selectedVenue === 'vsetin') && (
              <div className="flex flex-wrap gap-4">
                {selectedVenue === 'vsetin' && (
                  <a
                    href="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/dum19h3g_2026-12-04_greiner_packaging_slusovice_vecirek.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-400/50 text-emerald-300 px-6 py-3 rounded-xl transition-all backdrop-blur-sm text-center font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Předběžná CN (PDF)
                  </a>
                )}
                {currentVenue.pdfLayout && (
                  <a 
                    href={currentVenue.pdfLayout} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-xl transition-colors border border-white/10"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Layout míst k sezení (PDF)
                  </a>
                )}
                {currentVenue.virtualTour && (
                  <a 
                    href={currentVenue.virtualTour} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl transition-colors shadow-lg shadow-emerald-500/30"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Virtuální prohlídka 360°
                  </a>
                )}
                {currentVenue.conditionsDocument && (
                  <a 
                    href={currentVenue.conditionsDocument} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors shadow-lg shadow-blue-500/30"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Podmínky KD Fryšták
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <img 
                src={currentVenue.image}
                alt={`${currentVenue.name} - Hlavní sál s pódiem`}
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Photo Gallery for Fryšták */}
        {currentVenue.gallery && currentVenue.gallery.length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-semibold text-white mb-6 text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Fotogalerie {currentVenue.name}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {currentVenue.gallery.map((photo, index) => (
                <div 
                  key={index}
                  onClick={() => {
                    setLightboxImage(photo);
                    setLightboxOpen(true);
                  }}
                  className="relative overflow-hidden rounded-xl border border-white/10 hover:border-emerald-400/50 transition-all duration-300 group cursor-pointer"
                >
                  <img 
                    src={photo}
                    alt={`${currentVenue.name} - fotka ${index + 1}`}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  {/* Zoom icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={lightboxImage}
              alt="Zvětšený náhled"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Catering Section - Detailed Menu with Tabs
const CateringSection = ({ guestCount, setGuestCount, serviceType, setServiceType }) => {
  
  const BASE_PRICE_PER_PERSON = 1200; // Kč - základní cena cateringu
  const PREMIUM_SERVICE_EXTRA = 100; // Kč navýšení za sklo/porcelán (bez slevy)
  const BASE_GUEST_COUNT = 380; // Základní počet hostů pro výpočet poměru
  
  // Degresivní sleva: 300 osob = 0%, 500+ osob = 10%
  // Sleva se aplikuje POUZE na základní cenu (1200 Kč), ne na příplatek za sklo/porcelán
  const calculateDiscount = (guests) => {
    if (guests <= 300) return 0;
    if (guests >= 500) return 0.10;
    // Lineární interpolace mezi 300-500 osob
    return ((guests - 300) / (500 - 300)) * 0.10;
  };
  
  const discountPercent = calculateDiscount(guestCount);
  
  // Výpočet cen
  const basePriceWithDiscount = BASE_PRICE_PER_PERSON * (1 - discountPercent); // Sleva jen na základní cenu
  const pricePerPerson = serviceType === 'premium' 
    ? basePriceWithDiscount + PREMIUM_SERVICE_EXTRA  // Premium přidá +100 bez slevy
    : basePriceWithDiscount;
  
  const originalBaseTotalPrice = guestCount * BASE_PRICE_PER_PERSON; // Původní cena bez slevy
  const premiumTotalExtra = serviceType === 'premium' ? guestCount * PREMIUM_SERVICE_EXTRA : 0;
  const discountAmount = originalBaseTotalPrice * discountPercent; // Sleva pouze z základní ceny
  const finalTotalPrice = (originalBaseTotalPrice - discountAmount) + premiumTotalExtra;
  const finalPricePerPerson = pricePerPerson;

  // Function to scale quantities based on guest count
  const scaleQuantity = (baseCount) => {
    return Math.round((baseCount / BASE_GUEST_COUNT) * guestCount);
  };

  const coldBuffetItems = [
    { name: "Kuřecí banketní řízečky", portion: "50g", count: scaleQuantity(250), allergens: [1, 3, 7] },
    { name: "Vepřové banketní řízečky z pečeně", portion: "50g", count: scaleQuantity(120), allergens: [1, 3, 7] },
    { name: "Zelný salát coleslaw", portion: "100g", count: scaleQuantity(130), allergens: [3, 7, 10] },
    { name: "Míchaný zeleninový salát, balkánský sýr, medovo hořčičný dresing", portion: "100g", count: scaleQuantity(120), allergens: [7, 10] },
    { name: "Bramborový salát", portion: "100g", count: scaleQuantity(120), allergens: [3, 7, 10] },
    { name: "Tatarák, topinky", portion: "50g", count: scaleQuantity(100), allergens: [1, 3, 7, 9, 10] },
    { name: "Anglický roastbeef", portion: "50g", count: scaleQuantity(100), allergens: [7, 9] },
    { name: "Ovocná mísa z tuzemského a exotického ovoce", portion: "1000g", count: scaleQuantity(5), allergens: [] },
  ];

  const canapeItems = [
    { name: "Tartaletka s uzeným lososem zdobená koprem", portion: "50g", count: scaleQuantity(110), allergens: [1, 3, 4, 7] },
    { name: "Mini wrapy", portion: "50g", count: scaleQuantity(120), allergens: [1, 3, 7] },
    { name: "Mini burger", portion: "50g", count: scaleQuantity(130), allergens: [1, 3, 7, 10] },
  ];

  const hotBuffetItems = [
    { name: "Grilovaný losos s bylinkovou omáčkou", portion: "100g", count: scaleQuantity(70), allergens: [4, 7] },
    { name: "Medailonky z vepřové panenky, pepřová omáčka", portion: "100g", count: scaleQuantity(70), allergens: [7, 9] },
    { name: "Vepřová žebírka v BBQ marinádě", portion: "200g", count: scaleQuantity(100), allergens: [1, 7, 10] },
    { name: "Kuřecí roláda plněná vepřovým masem a špenátem", portion: "50g", count: scaleQuantity(70), allergens: [1, 7] },
    { name: "Kuřecí paličky ve sweet chilli omáčce", portion: "200g", count: scaleQuantity(70), allergens: [1, 6] },
    { name: "Mírně pikantní hověží gulášek", portion: "100g", count: scaleQuantity(20), allergens: [7, 9] },
    { name: "Pečené zelené lusky se slaninkou", portion: "100g", count: scaleQuantity(150), allergens: [] },
    { name: "Pečené brambory grenaille na bylinkách a másle", portion: "100g", count: scaleQuantity(300), allergens: [7] },
    { name: "Grilovaná sezonní zelenina", portion: "100g", count: scaleQuantity(150), allergens: [] },
    { name: "Raviolini s bůvolí ricottou a baby špenátem v sýrové omáčce", portion: "50g", count: scaleQuantity(50), allergens: [1, 3, 7] },
  ];

  const grillItems = [
    { name: "Vepřová kýta", portion: "1000g", count: scaleQuantity(7), allergens: [] },
    { name: "Nadívané selátko", portion: "1000g", count: scaleQuantity(7), allergens: [1, 3, 7] },
  ];

  const supplementsItems = [
    { name: "Koš čerstvého banketního pečiva", portion: "15g", count: scaleQuantity(350), allergens: [1, 7] },
    { name: "Studené omáčky k masu", portion: "100g", count: scaleQuantity(120), allergens: [3, 7, 10] },
    { name: "Sterilizovaná zelenina", portion: "50g", count: scaleQuantity(120), allergens: [] },
  ];

  const dessertItems = [
    { name: "Brownies malinové řezy", portion: "85g", count: scaleQuantity(75), allergens: [1, 3, 7, 8] },
    { name: "Jahodové řezy s podmáslím a smetanou", portion: "165g", count: scaleQuantity(75), allergens: [1, 3, 7] },
    { name: "Švarcvaldské řezy", portion: "110g", count: scaleQuantity(75), allergens: [1, 3, 7] },
    { name: "Řezy švestkového koláče s drobenkou", portion: "130g", count: scaleQuantity(75), allergens: [1, 3, 7] },
    { name: "Vlčnovské vdolečky", portion: "10g", count: scaleQuantity(350), allergens: [1, 3, 7] },
    { name: "Vánoční cukroví", portion: "50g", count: scaleQuantity(300), allergens: [1, 3, 7, 8] },
  ];

  const drinkItems = [
    { name: "Točené prosecco", portion: "100ml" },
    { name: "Sudové víno vinařství Vajbar (bílé, červené)", portion: "200ml" },
    { name: "Pivo Radegast 10", portion: "500ml" },
    { name: "Točený Birell pomelo grep", portion: "500ml" },
    { name: "Točená limonáda", portion: "500ml" },
    { name: "Voda neperlivá, perlivá", portion: "500ml" },
    { name: "Zrnková káva", portion: "100ml" },
  ];

  const overheadItems = [
    { name: "Vratné kelímky nápoje" },
    { name: "Jednorázové BIO obaly jídlo" },
    { name: "Cateringové vybavení" },
    { name: "Food truck 'mobilní kuchyně'" },
    { name: "Doprava catering" },
    { name: "Doprava personál" },
    { name: "Doprava Food truck" },
  ];

  const staffItems = [
    { name: "Kuchař", count: 2, hours: 8 },
    { name: "Pomocný kuchař", count: 3, hours: 8 },
    { name: "Debaras / obsluha nápojů", count: 6, hours: 8 },
  ];

  return (
    <section className="relative py-24 md:py-32 bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl text-slate-100 mb-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Catering & Servis
          </h2>
          <p 
            className="text-lg text-slate-400 max-w-2xl mx-auto mb-8"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Detailní rozpis jídla, nápojů a služeb
          </p>

          {/* Fresh Food Promise */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-emerald-900/20 backdrop-blur-xl border-2 border-emerald-400/30 rounded-2xl p-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Příprava přímo na místě
                </h3>
              </div>
              <p 
                className="text-center text-lg text-slate-200 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Veškeré jídlo připravujeme přímo na místě konání. Ručíme za čerstvost a špičkovou kvalitu.
              </p>
            </div>
          </div>

          {/* Catering Organization Note */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-blue-900/20 backdrop-blur-xl border border-blue-400/30 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <p 
                  className="text-slate-200 leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <strong className="text-white">Poznámka:</strong> Catering bude rozdělen na 2 sekce pro eliminaci počáteční fronty
                </p>
              </div>
            </div>
          </div>

          {/* Guest Count Control & Price Display */}
          <div className="max-w-4xl mx-auto mb-12">
            
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
              {/* Guest Count Slider */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <label 
                    className="text-lg font-semibold text-white"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Počet hostů
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setGuestCount(Math.max(100, guestCount - 10))}
                      className="bg-slate-800 hover:bg-slate-700 text-white rounded-full p-2 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span 
                      className="text-3xl font-bold text-emerald-400 min-w-[100px] text-center"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {guestCount}
                    </span>
                    <button
                      onClick={() => setGuestCount(Math.min(1000, guestCount + 10))}
                      className="bg-slate-800 hover:bg-slate-700 text-white rounded-full p-2 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <Slider
                  value={[guestCount]}
                  onValueChange={(value) => setGuestCount(value[0])}
                  min={100}
                  max={1000}
                  step={10}
                  className="w-full"
                />
              </div>

              {/* Service Type Selection */}
              <div className="mb-8 pb-8 border-b border-white/10">
                <label 
                  className="text-lg font-semibold text-white mb-4 block"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Typ servisu <span className="text-red-400">*</span>
                </label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Eco Service Option */}
                  <button
                    onClick={() => setServiceType('eco')}
                    className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                      serviceType === 'eco' 
                        ? 'border-emerald-500 bg-emerald-500/10' 
                        : 'border-white/10 bg-slate-800/50 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          serviceType === 'eco' ? 'border-emerald-500' : 'border-slate-400'
                        }`}>
                          {serviceType === 'eco' && (
                            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          Vratné kelímky & Bio obaly
                        </h3>
                      </div>
                      <span className="text-emerald-400 font-bold text-sm px-3 py-1 bg-emerald-500/20 rounded-full">
                        V CENĚ
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Ekologické řešení s vratným systémem a bio jednorázovými obaly catering kvality
                    </p>
                    <div className="text-emerald-400 font-mono font-bold text-2xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      0 Kč
                    </div>
                  </button>

                  {/* Premium Service Option */}
                  <button
                    onClick={() => setServiceType('premium')}
                    className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                      serviceType === 'premium' 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-white/10 bg-slate-800/50 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          serviceType === 'premium' ? 'border-amber-500' : 'border-slate-400'
                        }`}>
                          {serviceType === 'premium' && (
                            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          Sklo / Porcelán servis
                        </h3>
                      </div>
                      <span className="text-amber-400 font-bold text-sm px-3 py-1 bg-amber-500/20 rounded-full">
                        PREMIUM
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Kompletní servis do porcelánu, sklenic a kovových příborů
                    </p>
                    <div className="text-amber-400 font-mono font-bold text-2xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      +100 Kč/os
                    </div>
                  </button>
                </div>
              </div>

              {/* Price Summary with Discount */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
                {/* Price Per Person */}
                <div className="text-center">
                  <div className="text-slate-400 text-sm mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Cena na osobu
                  </div>
                  <div className="space-y-2">
                    {/* Price Per Person */}
                    <div 
                      className="text-4xl font-bold text-emerald-400"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {Math.round(finalPricePerPerson).toLocaleString('cs-CZ')} Kč
                    </div>
                  </div>
                </div>

                {/* Total Price */}
                <div className="text-center">
                  <div className="text-slate-400 text-sm mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Celková cena cateringu
                  </div>
                  <div className="space-y-2">
                    {/* Final Total */}
                    <div 
                      className="text-4xl font-bold"
                      style={{ 
                        fontFamily: "'JetBrains Mono', monospace",
                        background: 'linear-gradient(135deg, #d4af37 0%, #fcd34d 50%, #d4af37 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      {Math.round(finalTotalPrice).toLocaleString('cs-CZ')} Kč
                    </div>
                    
                    {/* Discount Info */}
                    {discountPercent > 0 && (
                      <div className="text-sm text-emerald-400 mt-2">
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          <span>Sleva {(discountPercent * 100).toFixed(0)}% ({Math.round(discountAmount).toLocaleString('cs-CZ')} Kč)</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Pro {guestCount} hostů
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="food" className="w-full">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-4 mb-12 bg-slate-900/60 backdrop-blur-xl border border-white/10 p-1 rounded-full">
            <TabsTrigger value="food" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Jídlo
            </TabsTrigger>
            <TabsTrigger value="drinks" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Nápoje
            </TabsTrigger>
            <TabsTrigger value="desserts" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Dezerty
            </TabsTrigger>
            <TabsTrigger value="logistics" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Režie
            </TabsTrigger>
          </TabsList>

          {/* Food Tab */}
          <TabsContent value="food" className="mt-0">
            <div className="space-y-8">
              {/* Cold Buffet */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 mb-6">
                <img 
                  src="/cold-buffet-new.jpg"
                  alt="Studený raut"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Studený raut
                  </h3>
                  <p className="text-slate-300">Řízečky, saláty a další speciality</p>
                </div>
              </div>
              
              <MenuCategory 
                title="Studený raut" 
                icon={<Utensils className="w-6 h-6 text-emerald-400" />}
                items={coldBuffetItems} 
              />
              
              {/* Canapes */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 mb-6">
                <img 
                  src="/canapes-salmon.jpg"
                  alt="Tartaletka s uzeným lososem"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Exkluzivní kanapky
                  </h3>
                  <p className="text-slate-300">Tartaletky s uzeným lososem a další speciality</p>
                </div>
              </div>
              
              <MenuCategory 
                title="Kanapky" 
                icon={<Utensils className="w-6 h-6 text-emerald-400" />}
                items={canapeItems} 
              />
              
              {/* Hot Buffet */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 mb-6">
                <img 
                  src="/salmon-grill.jpg"
                  alt="Grilovaný losos s bylinkovou omáčkou"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Teplý raut
                  </h3>
                  <p className="text-slate-300">Grilovaný losos s bylinkovou omáčkou</p>
                </div>
              </div>
              
              <MenuCategory 
                title="Teplý raut" 
                icon={<Utensils className="w-6 h-6 text-emerald-400" />}
                items={hotBuffetItems} 
              />
              
              {/* Grill */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 mb-6">
                <img 
                  src="/pork-carving-new.jpg"
                  alt="Vepřová kýta krájená před hosty"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Maso grilované v celku
                  </h3>
                  <p className="text-slate-300">Vepřová kýta, nadívané selátko - krájené před hosty</p>
                </div>
              </div>
              
              <MenuCategory 
                title="Maso grilované v celku" 
                icon={<Utensils className="w-6 h-6 text-emerald-400" />}
                items={grillItems} 
              />
              
              {/* Supplements */}
              <MenuCategory 
                title="Doplňky" 
                icon={<Utensils className="w-6 h-6 text-emerald-400" />}
                items={supplementsItems} 
              />

              {/* Allergen Legend */}
              <AllergenLegend />
            </div>
          </TabsContent>

          {/* Drinks Tab */}
          <TabsContent value="drinks" className="mt-0">
            {/* Unlimited Drinks Badge */}
            <div className="mb-8 text-center">
              <div className="inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white px-8 py-4 rounded-2xl font-bold text-xl shadow-2xl animate-pulse" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <div className="flex items-center gap-3">
                  <Wine className="w-8 h-8" />
                  <span>🍾 Všechny nápoje neomezeně V CENĚ</span>
                  <Wine className="w-8 h-8" />
                </div>
              </div>
              <p className="text-emerald-300 text-sm mt-3 font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Zahrnuto v ceně cateringu - pijte bez omezení!
              </p>
            </div>

            {/* Beer Jet Video */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
              <div className="mb-4">
                <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Beer Jet - Světový unikát v čepování piva
                </h3>
                <p className="text-emerald-300 text-lg font-bold">✨ Součástí servisu!</p>
              </div>
              
              {/* Responsive Video Container */}
              <div className="relative overflow-hidden rounded-xl" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/z6xkTiPJ968?si=ReNGEIvr3xIhLlnW" 
                  title="YouTube video player" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen
                />
              </div>
            </div>
            
            <UnlimitedDrinksCategory 
              title="Nabídka nápojů" 
              icon={<Wine className="w-6 h-6 text-emerald-400" />}
              items={drinkItems} 
            />

            {/* Stock Guarantee Note */}
            <div className="mt-6 bg-emerald-900/20 backdrop-blur-xl border border-emerald-400/30 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-emerald-400 mt-1" />
                </div>
                <div>
                  <h4 
                    className="text-lg font-semibold text-emerald-300 mb-3"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Dostatečné zásoby zaručeny
                  </h4>
                  <p className="text-slate-300 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Na základě zkušeností z minulé akce (kdy došlo točené prosecco) jsme upravili zásoby. 
                    <strong className="text-white"> Zajistíme dostatečné množství všech nápojů po celou dobu akce.</strong>
                  </p>
                  <p className="text-emerald-300 font-semibold text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    ✓ Navýšené zásoby prosecca a vína<br/>
                    ✓ Průběžné doplňování během akce<br/>
                    ✓ Záložní zásoby připraveny
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Drinks - Cocktails & Rum */}
            <div className="bg-emerald-900/30 border border-emerald-400/30 rounded-2xl p-8 mt-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Wine className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h4 className="text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Nápoje mimo nabídku
                  </h4>
                </div>
                <p className="text-slate-300 text-lg leading-relaxed">
                  V případě vašeho zájmu nabízíme možnost <span className="text-emerald-400 font-semibold">koktejlů a vybraných rumů</span> jako rozšíření nabídky. Host má možnost si nápoj zakoupit.
                </p>
              </div>

              {/* Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative overflow-hidden rounded-xl border border-white/10 hover:border-emerald-400/50 transition-all duration-300">
                  <img 
                    src="/koktejl-dodatek.jpg"
                    alt="Koktejly"
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Koktejly
                    </p>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl border border-white/10 hover:border-emerald-400/50 transition-all duration-300">
                  <img 
                    src="/rum-dodatek.png"
                    alt="Vybrané rumy"
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Vybrané rumy
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Desserts Tab */}
          <TabsContent value="desserts" className="mt-0">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 mb-6">
              <img 
                src="/caramel-cake-new.jpg"
                alt="Karamelový dort s datlemi"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <h3 className="font-serif text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Exkluzivní dezerty
                </h3>
                <p className="text-slate-300">Karamelový dort s datlemi a další sladké speciality</p>
              </div>
            </div>
            
            <MenuCategory 
              title="Dezerty" 
              icon={<IceCream className="w-6 h-6 text-emerald-400" />}
              items={dessertItems} 
            />
            
            {/* Allergen Legend */}
            <AllergenLegend />
          </TabsContent>

          {/* Logistics Tab */}
          <TabsContent value="logistics" className="mt-0">
            <div className="space-y-8">
              <LogisticsCategory 
                title="Režie" 
                items={overheadItems} 
              />
              
              <StaffCategory 
                title="Personál" 
                items={staffItems} 
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

// Menu Category Component
const MenuCategory = ({ title, icon, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h3 
          className="text-2xl font-semibold text-white"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Položka
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Gramáž
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Počet porcí
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 px-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.name}
                  {item.allergens && item.allergens.length > 0 && (
                    <span className="text-amber-400 text-xs ml-2 font-semibold">
                      ({item.allergens.join(', ')})
                    </span>
                  )}
                </td>
                <td className="py-3 px-2 text-center text-emerald-400 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.portion}
                </td>
                <td className="py-3 px-2 text-center text-white font-mono font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Allergen Legend Component
const AllergenLegend = () => {
  const allergens = [
    { num: 1, name: "Obiloviny obsahující lepek" },
    { num: 2, name: "Korýši a výrobky z nich" },
    { num: 3, name: "Vejce a výrobky z nich" },
    { num: 4, name: "Ryby a výrobky z nich" },
    { num: 5, name: "Arašídy a výrobky z nich" },
    { num: 6, name: "Sójové boby (sója) a výrobky z nich" },
    { num: 7, name: "Mléko a výrobky z něj (laktóza)" },
    { num: 8, name: "Skořápkové plody (ořechy)" },
    { num: 9, name: "Celer a výrobky z něj" },
    { num: 10, name: "Hořčice a výrobky z ní" },
    { num: 11, name: "Sezamová semena (sezam) a výrobky z nich" },
    { num: 12, name: "Oxid siřičitý a siřičitany" },
    { num: 13, name: "Vlčí bob (lupina) a výrobky z něj" },
    { num: 14, name: "Měkkýši a výrobky z nich" },
  ];

  return (
    <div className="bg-amber-900/20 backdrop-blur-xl border border-amber-400/30 rounded-2xl p-6 mt-8">
      <h4 
        className="text-lg font-semibold text-amber-300 mb-4 flex items-center gap-2"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        <Utensils className="w-5 h-5" />
        Alergeny
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {allergens.map((allergen) => (
          <div key={allergen.num} className="text-sm text-slate-300 flex gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <span className="text-amber-400 font-bold min-w-[20px]">{allergen.num}.</span>
            <span>{allergen.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Drinks Category Component (different column headers)
const DrinksCategory = ({ title, icon, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h3 
          className="text-2xl font-semibold text-white"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Položka
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Objem
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Počet
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 px-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.name}
                </td>
                <td className="py-3 px-2 text-center text-emerald-400 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.portion}
                </td>
                <td className="py-3 px-2 text-center text-white font-mono font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Unlimited Drinks Category Component (no count column)
const UnlimitedDrinksCategory = ({ title, icon, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h3 
          className="text-2xl font-semibold text-white"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </h3>
        <span className="ml-auto bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-bold">
          NEOMEZENÉ
        </span>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Položka
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Objem
              </th>
              <th className="text-center py-3 px-2 text-emerald-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Dostupnost
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 px-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.name}
                </td>
                <td className="py-3 px-2 text-center text-emerald-400 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.portion}
                </td>
                <td className="py-3 px-2 text-center">
                  <span className="inline-block bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    ∞ NEOMEZENÉ
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Logistics Category Component (no count column)
const LogisticsCategory = ({ title, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Building className="w-6 h-6 text-emerald-400" />
        <h3 
          className="text-2xl font-semibold text-white"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <ul className="space-y-3">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-3 py-2 text-slate-300 border-b border-white/5" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <span className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></span>
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Staff Category Component (no hours column)
const StaffCategory = ({ title, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-emerald-400" />
        <h3 
          className="text-2xl font-semibold text-white"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {title}
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Pozice
              </th>
              <th className="text-center py-3 px-2 text-slate-400 font-medium text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Počet osob
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 px-2 text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.name}
                </td>
                <td className="py-3 px-2 text-center text-white font-mono font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// Program Section - Detailed Schedule
const ProgramSection = ({ selectedHeadliner, selectedSupportBand }) => {
  const headlinerName = selectedHeadliner ? selectedHeadliner.name : "HEADLINER";
  const supportBandName = selectedSupportBand ? selectedSupportBand.name : "REGIONÁLNÍ KAPELA";
  
  return (
    <section className="relative py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl text-slate-100 mb-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Pracovní harmonogram
          </h2>
          <p 
            className="text-lg text-slate-400 max-w-2xl mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Úpravy dle konzultace
          </p>
        </div>

        {/* Preparation Schedule */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-emerald-400 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Harmonogram příprav a úklidu
          </h3>
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex gap-4">
              <span className="text-emerald-400 font-mono font-bold min-w-[140px]">8:00–16:00</span>
              <p className="text-slate-300">Technické zabezpečení akce, výzdoba, catering, zvukové zkoušky</p>
            </div>
            <div className="flex gap-4">
              <span className="text-emerald-400 font-mono font-bold min-w-[140px]">0:00–04:00</span>
              <p className="text-slate-300">Úklid prostoru</p>
            </div>
          </div>
        </div>

        {/* Sound Checks */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-emerald-400 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Zvukové zkoušky
          </h3>
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex gap-4">
              <span className="text-emerald-400 font-mono font-bold min-w-[140px]">13:00–14:30</span>
              <p className="text-slate-300">Zvuková zkouška {supportBandName} <span className="text-slate-500">/příjezd 12:00/</span></p>
            </div>
            <div className="flex gap-4">
              <span className="text-emerald-400 font-mono font-bold min-w-[140px]">15:00–16:00</span>
              <p className="text-slate-300">Zvuková zkouška {headlinerName} <span className="text-slate-500">/příjezd 14:00/</span></p>
            </div>
            <div className="flex gap-4">
              <span className="text-emerald-400 font-mono font-bold min-w-[140px]">16:30–16:40</span>
              <p className="text-slate-300">DJ Ondřej Albrecht + moderace <span className="text-slate-500">/příjezd 15:45/</span></p>
            </div>
          </div>
        </div>

        {/* Main Program */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-emerald-400 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Hlavní program večírku
          </h3>
          
          {/* Main Hall */}
          <div className="mb-8">
            <h4 className="text-xl font-semibold text-white mb-4 pl-4 border-l-4 border-emerald-400">Hlavní sál</h4>
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-3">
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">17:40–18:00</span>
                <p className="text-slate-300">Podkresová hudba v hlavním sále</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">18:00–18:45</span>
                <p className="text-slate-300">Příchod hostů, registrace, otevření barů, focení u foto stěny, ochutnávka vánočního cukroví a kanapek ve foyer</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">18:45–19:00</span>
                <p className="text-slate-300">Oficiální zahájení večírku, přivítání moderátorem <span className="text-slate-500">/hudba na příchod/</span>. Proslov vedení společnosti (CEO)</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">19:00–19:45</span>
                <p className="text-slate-300">DJ</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">19:45–20:45</span>
                <p className="text-slate-300">{supportBandName} set 1</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">20:45–21:00</span>
                <p className="text-slate-300">Podkresová hudba</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">21:00–21:45</span>
                <p className="text-slate-300">{supportBandName} set 2</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">22:00–23:00</span>
                <p className="text-slate-300 font-semibold">{headlinerName}</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">23:00–23:45</span>
                <p className="text-slate-300">DJ</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">24:00</span>
                <p className="text-slate-300 font-semibold">Ukončení večírku</p>
              </div>
            </div>
          </div>

          {/* Foyer */}
          <div>
            <h4 className="text-xl font-semibold text-white mb-4 pl-4 border-l-4 border-emerald-400">Foyer</h4>
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-3">
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">17:30–23:30</span>
                <p className="text-slate-300">Foto stěna Greiner</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">17:30–24:00</span>
                <p className="text-slate-300">Nápojový bar</p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">19:00–23:30</span>
                <p className="text-slate-300">Raut ve foyer <span className="text-slate-500">(rozdělení na 2 sekce)</span></p>
              </div>
              <div className="flex gap-4">
                <span className="text-emerald-400 font-mono font-bold min-w-[140px]">19:00–23:00</span>
                <p className="text-slate-300">Foto koutek s obsluhou</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Band Card Component
const BandCard = ({ band, selectedBand, onSelect }) => {
  const isSelected = selectedBand?.id === band.id;
  
  return (
    <button
      onClick={() => onSelect(band)}
      className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 text-left ${
        isSelected
          ? 'border-emerald-500 shadow-xl shadow-emerald-500/20'
          : 'border-white/10 hover:border-white/30'
      }`}
    >
      {/* Band Image */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src={band.image}
          alt={band.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        
        {/* Selected Badge */}
        {isSelected && (
          <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold">
            ✓ VYBRÁNO
          </div>
        )}

        {/* Band Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 
            className="text-2xl font-bold text-white mb-1"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {band.name}
          </h3>
          <p className="text-emerald-400 text-sm font-semibold mb-2">
            {band.genre}
          </p>
        </div>
      </div>

      {/* Price Section */}
      <div className="bg-slate-900/80 backdrop-blur-xl p-4">
        <p className="text-slate-400 text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {band.description}
        </p>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Honorář:</span>
            <span className="text-white font-mono">{band.price.toLocaleString('cs-CZ')} Kč</span>
          </div>
          {band.accommodation > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Ubytování:</span>
              <span className="text-white font-mono">{band.accommodation.toLocaleString('cs-CZ')} Kč</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold pt-2 border-t border-white/10">
            <span className="text-emerald-400">Celkem:</span>
            <span className="text-emerald-400 font-mono">
              {(band.price + band.accommodation).toLocaleString('cs-CZ')} Kč
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

// Band Selection Section
const BandSelectionSection = ({ selectedHeadliner, setSelectedHeadliner, selectedSupportBand, setSelectedSupportBand }) => {

  const bands = [
    {
      id: 'mig21',
      name: 'Mig 21',
      price: 422000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/sc4mn0pf_mig21fotka.jpg',
      description: 'Legendární česká popová kapela s energickými koncerty a osvědčenými hity',
      genre: 'Rock/Pop',
      type: 'headliner'
    },
    {
      id: 'mirai',
      name: 'Mirai',
      price: 290000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/vtwjkopp_mirai.jpg',
      description: 'Vystoupení známého zpěváka Mirai s kytaristou',
      genre: 'Pop-Rock',
      type: 'headliner'
    },
    {
      id: 'queenie',
      name: 'Queenie',
      price: 266000,
      accommodation: 15000,
      image: '/queenie.jpg',
      description: 'Nejlepší Queen tribute show v České republice',
      genre: 'Tribute/Rock',
      type: 'headliner'
    },
    {
      id: 'xindl-x',
      name: 'Xindl X',
      price: 380000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/zfhzgkhw_xindl%20x.JPG',
      description: 'Oceňovaný písničkář s multižánrovým stylem od popu přes blues až folk',
      genre: 'Pop/Folk/Blues',
      type: 'headliner'
    },
    {
      id: 'kapitan-demo',
      name: 'Kapitán Demo',
      price: 220000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/k5lk9cm5_demo.jpg',
      description: 'Humorně-rapový fenomén s absurdním humorem a hity jako "Zlatíčka"',
      genre: 'Comedy Rap/Hip-Hop',
      type: 'headliner'
    },
    {
      id: 'jelen',
      name: 'Jelen',
      price: 480000,
      accommodation: 15000,
      image: '/jelen.jpg',
      description: 'Česká rocková kapela s energickým live vystoupením',
      genre: 'Rock/Alternative',
      type: 'headliner'
    },
    {
      id: 'silver-spoon',
      name: 'The Silver Spoon',
      price: 89000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/opqak59b_silver%20spoon.jpg',
      description: 'Indie rocková kapela s energickými melodiemi a tanečním projevem',
      genre: 'Indie Rock',
      type: 'headliner'
    },
    {
      id: 'david-kraus',
      name: 'David Kraus',
      price: 160000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/2gdveori_david%20kraus.jpg',
      description: 'Populární zpěvák známý z Laura a její tygři, hitů jako Zuzana Norisová',
      genre: 'Pop/Alternative',
      type: 'headliner'
    },
    {
      id: 'civilni-obrana',
      name: 'Civilní Obrana',
      price: 110000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/kwnll8io_civilni%20obrana%20foto.jpg',
      description: 'Pop-punková skupina z Čáslavi s energickým živým vystoupením',
      genre: 'Pop-Punk',
      type: 'headliner'
    },
    {
      id: 'koblizci',
      name: 'Koblížci',
      price: 85000,
      accommodation: 15000,
      image: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/cd3e5kxw_koblizci.jpeg',
      description: 'Pop-punková kapela s prvky elektroniky, známá hity z 30leté kariéry',
      genre: 'Pop-Punk/Electronic',
      type: 'headliner'
    },
    {
      id: 'reflexy',
      name: 'Reflexy',
      price: 45000,
      accommodation: 0,
      image: '/reflexy.jpg',
      description: 'Zlínská kapela s vlastní tvorbou - hit "Ty jsi divoká"',
      genre: 'Pop/Rock',
      type: 'support'
    },
    {
      id: 'focus-rock',
      name: 'Focus Rock',
      price: 30000,
      accommodation: 0,
      image: '/focus-rock.jpg',
      description: 'Zlínská rocková kapela - harlejrock/punk',
      genre: 'Rock/Punk',
      type: 'support'
    }
  ];

  const headliners = bands.filter(b => b.type === 'headliner');
  const supportBands = bands.filter(b => b.type === 'support');

  const totalHeadlinerCost = selectedHeadliner 
    ? selectedHeadliner.price + selectedHeadliner.accommodation 
    : 0;
  
  const totalSupportCost = selectedSupportBand 
    ? selectedSupportBand.price + selectedSupportBand.accommodation 
    : 0;
  
  const totalBandsCost = totalHeadlinerCost + totalSupportCost;

  return (
    <section className="relative py-24 md:py-32 bg-[#020617]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl text-slate-100 mb-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Výběr headlinera
          </h2>
          <p 
            className="text-lg text-slate-400 max-w-2xl mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Vyberte si kapelu, která roztančí vaše hosty
          </p>
        </div>

        {/* Headliners Section */}
        <div className="mb-16">
          <h3 
            className="text-2xl font-bold text-white mb-6 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Headlineři
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {headliners.map((band) => (
              <BandCard 
                key={band.id} 
                band={band} 
                selectedBand={selectedHeadliner} 
                onSelect={setSelectedHeadliner}
              />
            ))}
          </div>
        </div>

        {/* Support Bands Section */}
        <div className="mb-12">
          <h3 
            className="text-2xl font-bold text-white mb-6 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Doprovodné kapely
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {supportBands.map((band) => (
              <BandCard 
                key={band.id} 
                band={band} 
                selectedBand={selectedSupportBand} 
                onSelect={setSelectedSupportBand}
              />
            ))}
          </div>
        </div>

        {/* Additional Services Section - Visual Cards */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h3 
            className="text-2xl font-bold text-white mb-6 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Další služby (pevná součást nabídky)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Moderator Card */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-400/30 rounded-2xl overflow-hidden">
              <div className="relative h-64">
                <img 
                  src="/dj-ondrej.jpg"
                  alt="Ondřej Albrecht - Moderátor & DJ"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
                <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  ✓ ZAHRNUTO
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Ondřej Albrecht
                  </h3>
                  <p className="text-emerald-400 text-sm font-semibold">
                    Moderátor & DJ (Rádio Frekvence 1)
                  </p>
                </div>
              </div>
              <div className="bg-slate-900/80 p-4">
                <p className="text-slate-400 text-sm mb-3">
                  Profesionální moderace večera a DJ služby od známého rádiového moderátora
                </p>
                <div className="flex justify-between items-center font-bold text-lg border-t border-white/10 pt-3">
                  <span className="text-emerald-400">Cena:</span>
                  <span className="text-emerald-400 font-mono">20 000 Kč</span>
                </div>
              </div>
            </div>

            {/* Photobooth Card */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-400/30 rounded-2xl overflow-hidden">
              <div className="relative h-64">
                <img 
                  src="/fotkoutek.jpg"
                  alt="Fotkoutek s green screen"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
                <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  ✓ ZAHRNUTO
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Fotkoutek
                  </h3>
                  <p className="text-emerald-400 text-sm font-semibold">
                    Green Screen & Rekvizity
                  </p>
                </div>
              </div>
              <div className="bg-slate-900/80 p-4">
                <p className="text-slate-400 text-sm mb-3">
                  Green screen s volbou více pozadí, kompletní sada rekvizit pro zábavné fotky
                </p>
                <div className="flex justify-between items-center font-bold text-lg border-t border-white/10 pt-3">
                  <span className="text-emerald-400">Cena:</span>
                  <span className="text-emerald-400 font-mono">15 000 Kč</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Bands Summary */}
        {(selectedHeadliner || selectedSupportBand) && (
          <div className="bg-emerald-900/20 backdrop-blur-xl border border-emerald-400/30 rounded-2xl p-8">
            <h3 
              className="text-2xl font-bold text-white mb-6 text-center"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Váš výběr kapel
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Headliner */}
              <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                <h4 className="text-lg font-bold text-emerald-400 mb-3">
                  {selectedHeadliner ? '✓ Headliner' : 'Headliner (nevybrán)'}
                </h4>
                {selectedHeadliner ? (
                  <>
                    <p className="text-xl font-bold text-white mb-1">{selectedHeadliner.name}</p>
                    <p className="text-sm text-slate-400 mb-3">{selectedHeadliner.description}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Honorář:</span>
                        <span className="text-white font-mono">{selectedHeadliner.price.toLocaleString('cs-CZ')} Kč</span>
                      </div>
                      {selectedHeadliner.accommodation > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Ubytování:</span>
                          <span className="text-white font-mono">{selectedHeadliner.accommodation.toLocaleString('cs-CZ')} Kč</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                        <span className="text-emerald-400">Celkem:</span>
                        <span className="text-emerald-400 font-mono">
                          {totalHeadlinerCost.toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-sm">Vyberte headlinera pro váš večírek</p>
                )}
              </div>

              {/* Support Band */}
              <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                <h4 className="text-lg font-bold text-emerald-400 mb-3">
                  {selectedSupportBand ? '✓ Doprovodná kapela' : 'Doprovodná kapela (nevybrána)'}
                </h4>
                {selectedSupportBand ? (
                  <>
                    <p className="text-xl font-bold text-white mb-1">{selectedSupportBand.name}</p>
                    <p className="text-sm text-slate-400 mb-3">{selectedSupportBand.description}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Honorář:</span>
                        <span className="text-white font-mono">{selectedSupportBand.price.toLocaleString('cs-CZ')} Kč</span>
                      </div>
                      {selectedSupportBand.accommodation > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Ubytování:</span>
                          <span className="text-white font-mono">{selectedSupportBand.accommodation.toLocaleString('cs-CZ')} Kč</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                        <span className="text-emerald-400">Celkem:</span>
                        <span className="text-emerald-400 font-mono">
                          {totalSupportCost.toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-sm">Vyberte doprovodnou kapelu (volitelné)</p>
                )}
              </div>
            </div>

            {/* Fixed Services - Always Included */}
            <div className="mt-6">
              <h4 className="text-lg font-bold text-white mb-4 text-center">
                Další služby <span className="text-emerald-400">(zahrnuto v nabídce)</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Moderator & DJ */}
                <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-400/20 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-emerald-400 text-xl">✓</span>
                    <h4 className="text-lg font-bold text-white">Ondřej Albrecht</h4>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">Moderátor & DJ (Rádio Frekvence 1)</p>
                  <div className="flex justify-between items-center pt-3 border-t border-white/10">
                    <span className="text-slate-400 text-sm">Cena:</span>
                    <span className="text-emerald-400 font-mono font-bold">20 000 Kč</span>
                  </div>
                </div>

                {/* Photobooth */}
                <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-400/20 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-emerald-400 text-xl">✓</span>
                    <h4 className="text-lg font-bold text-white">Fotokoutek</h4>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">Green Screen & Rekvizity</p>
                  <div className="flex justify-between items-center pt-3 border-t border-white/10">
                    <span className="text-slate-400 text-sm">Cena:</span>
                    <span className="text-emerald-400 font-mono font-bold">15 000 Kč</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-emerald-800/40 rounded-xl p-6 text-center">
              <div className="text-slate-300 text-sm mb-2">Celkové náklady na program</div>
              <div 
                className="text-5xl font-bold text-emerald-400"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {(totalBandsCost + 35000).toLocaleString('cs-CZ')} Kč
              </div>
              <div className="text-slate-400 text-xs mt-2">
                (kapely + moderátor + fotokoutek, vč. ubytování)
              </div>
            </div>
          </div>
        )}

        {/* No Selection Message */}
        {!selectedHeadliner && !selectedSupportBand && (
          <div className="text-center bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <p className="text-slate-400" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Vyberte headlinera a volitelně doprovodnou kapelu pro váš večírek
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

// Technical Production Section
const TechSection = () => {
  return (
    <section className="relative py-24 md:py-32 bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl text-slate-100 mb-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Technické zajištění
          </h2>
          <p 
            className="text-lg text-slate-400 max-w-2xl mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Profesionální zvuk, světla a stage design
          </p>
        </div>

        {/* Tech Grid - All cards in one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <TechCard 
            icon={<Volume2 className="w-12 h-12 text-emerald-400" />}
            title="Zvuk & Světla"
            items={[
              "Zvukový systém EAW linearray",
              "Otočné hlavy SPOT, WASH",
              "Vše dle rideru headlinera"
            ]}
          />
          <TechCard 
            icon={<MonitorPlay className="w-12 h-12 text-emerald-400" />}
            title="LED Wall"
            items={["5x3m LED stěna", "Projekce logo", "Live camera"]}
          />
          <TechCard 
            icon={<Sparkles className="w-12 h-12 text-emerald-400" />}
            title="Výzdoba akce"
            items={["Dekorace stolů", "Dekorace pódia", "Výzdoba vstupních prostor"]}
          />
          <TechCard 
            icon={<Building className="w-12 h-12 text-emerald-400" />}
            title="Inventář"
            items={[
              "Stoly, židle",
              "Ubrusy, rautové sukně",
              "Bistro stolky, potahy"
            ]}
          />
        </div>

        {/* Featured Image */}
        <div className="mt-12 relative overflow-hidden rounded-2xl border border-white/10">
          <img 
            src="https://images.unsplash.com/photo-1762870288146-574bb53c9a3c?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Live Band Performance"
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
          <div className="absolute bottom-8 left-8">
            <h3 className="font-serif text-3xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Profesionální produkce
            </h3>
            <p className="text-slate-300 text-lg">15+ členů produkčního týmu</p>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h3 
              className="text-2xl font-bold text-white mb-6 text-center"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Cenový rozpis techniky
            </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Hliníkové konstrukce na pódiu pro zavěšení osvětlení a aparatury, a LED stěny
                </span>
                <span className="text-white font-mono font-semibold text-lg">10 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Osvětlení dle rideru headlinera - přední i zadní rampa se SPOT, WASH, BLINDER apod
                </span>
                <span className="text-white font-mono font-semibold text-lg">20 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Ozvučení akce dle rideru headlinera - Linearray system EAW
                </span>
                <span className="text-white font-mono font-semibold text-lg">25 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Instalace, deinstalace výše uvedeného
                </span>
                <span className="text-white font-mono font-semibold text-lg">5 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Doprava dodávka do 3.5t (konstrukce)
                </span>
                <span className="text-white font-mono font-semibold text-lg">2 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Doprava dodávka do 3.5t (zvuk a světla)
                </span>
                <span className="text-white font-mono font-semibold text-lg">4 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Doprava dodávka do 3.5t (led stěna)
                </span>
                <span className="text-white font-mono font-semibold text-lg">3 000 Kč</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-white/5">
                <span className="text-slate-300" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  LED stěna 5×3m včetně kamery a režie
                </span>
                <span className="text-white font-mono font-semibold text-lg">34 000 Kč</span>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-emerald-800/40 rounded-xl p-6 mb-4">
              <div className="flex justify-between items-center">
                <span 
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Celkem za techniku
                </span>
                <span 
                  className="text-4xl font-bold text-emerald-400"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  103 000 Kč
                </span>
              </div>
            </div>

            {/* Note */}
            <div className="text-center">
              <p className="text-slate-400 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                * V ceně již kompletní personál na obsluhu techniky
              </p>
            </div>
          </div>
        </div>

        {/* Inventory Rental Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h3 
              className="text-2xl font-bold text-white mb-6 text-center"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Pronájem inventáře pro hosty
            </h3>
            
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 mb-4 pb-3 border-b border-white/20">
              <span className="text-slate-400 text-sm font-semibold">Položka</span>
              <span className="text-slate-400 text-sm font-semibold text-center">Počet</span>
              <span className="text-slate-400 text-sm font-semibold text-right">Cena</span>
              <span className="text-slate-400 text-sm font-semibold text-right">Cena celkem</span>
            </div>

            {/* Table Rows */}
            <div className="space-y-3 mb-6">
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Bistro stolky s bílým potahem</span>
                <span className="text-white font-mono text-sm text-center">20</span>
                <span className="text-white font-mono text-sm text-right">350 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">7 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Cateringové stoly</span>
                <span className="text-white font-mono text-sm text-center">20</span>
                <span className="text-white font-mono text-sm text-right">250 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">5 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Bílé ubrusy na cateringové stoly</span>
                <span className="text-white font-mono text-sm text-center">20</span>
                <span className="text-white font-mono text-sm text-right">120 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">2 400 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Rautové sukně</span>
                <span className="text-white font-mono text-sm text-center">10</span>
                <span className="text-white font-mono text-sm text-right">400 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">4 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Doprava inventáře dodávka do 3.5t</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">1 760 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">1 760 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Odvoz/ likvidace odpadu</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">1 000 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">1 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Koše vč. pytlů</span>
                <span className="text-white font-mono text-sm text-center">15</span>
                <span className="text-white font-mono text-sm text-right">150 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">2 250 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Instalace, deinstalace inventáře výše uvedeného</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">4 000 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">4 000 Kč</span>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-emerald-800/40 rounded-xl p-6">
              <div className="flex justify-between items-center">
                <span 
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Celkem za inventář
                </span>
                <span 
                  className="text-4xl font-bold text-emerald-400"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  27 410 Kč
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Decoration Section */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h3 
              className="text-2xl font-bold text-white mb-6 text-center"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Dekorace stolů a prostoru
            </h3>
            
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 mb-4 pb-3 border-b border-white/20">
              <span className="text-slate-400 text-sm font-semibold">Položka</span>
              <span className="text-slate-400 text-sm font-semibold text-center">Počet</span>
              <span className="text-slate-400 text-sm font-semibold text-right">Cena</span>
              <span className="text-slate-400 text-sm font-semibold text-right">Cena celkem</span>
            </div>

            {/* Table Rows */}
            <div className="space-y-3 mb-6">
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Oranžové mašle na bistrostolky</span>
                <span className="text-white font-mono text-sm text-center">20</span>
                <span className="text-white font-mono text-sm text-right">40 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">800 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Oranžové běhouny na stoly</span>
                <span className="text-white font-mono text-sm text-center">114</span>
                <span className="text-white font-mono text-sm text-right">40 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">4 560 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">LED svíčky na stolech</span>
                <span className="text-white font-mono text-sm text-center">91</span>
                <span className="text-white font-mono text-sm text-right">20 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">1 820 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">4 vrstvé tvarované ubrousky na stolech (barva dle domluvy)</span>
                <span className="text-white font-mono text-sm text-center">400</span>
                <span className="text-white font-mono text-sm text-right">10 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">4 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Balónková stěna s potiskem GREINER ASSISTEC VÁNOČNÍ VEČÍREK</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">5 000 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">5 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Balónkové sloupy na pódiu červeno-bílo-zlaté s logem Greiner</span>
                <span className="text-white font-mono text-sm text-center">2</span>
                <span className="text-white font-mono text-sm text-right">1 500 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">3 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Balónková dekorace v prostorách KD (héliové balonky)</span>
                <span className="text-white font-mono text-sm text-center">50</span>
                <span className="text-white font-mono text-sm text-right">40 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">2 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Světelné řetězy na balkonech</span>
                <span className="text-white font-mono text-sm text-center">7</span>
                <span className="text-white font-mono text-sm text-right">650 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">4 550 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Světelný řetěz na pódiu</span>
                <span className="text-white font-mono text-sm text-center">3</span>
                <span className="text-white font-mono text-sm text-right">650 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">1 950 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Grafické zpracování (pozvánka, program, menu apod.)</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">3 500 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">3 500 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5">
                <span className="text-slate-300 text-sm">Tiskoviny (program, menu na stolech)</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">3 000 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">3 000 Kč</span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 py-2 border-b border-white/5 bg-emerald-900/10">
                <span className="text-slate-300 text-sm font-semibold">Personál - dekorování, instalace světýlek, vázání mašlí, příprava prostor</span>
                <span className="text-white font-mono text-sm text-center">1</span>
                <span className="text-white font-mono text-sm text-right">12 000 Kč</span>
                <span className="text-white font-mono font-semibold text-sm text-right">12 000 Kč</span>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-emerald-800/40 rounded-xl p-6">
              <div className="flex justify-between items-center">
                <span 
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Celkem za výzdobu
                </span>
                <span 
                  className="text-4xl font-bold text-emerald-400"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  46 180 Kč
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reusable Tech Card Component
const TechCard = ({ icon, title, items }) => {
  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-emerald-400/50 transition-all duration-300 hover:-translate-y-2">
      <div className="mb-6">{icon}</div>
      <h3 
        className="text-2xl font-semibold text-white mb-4"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="text-slate-400 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
            <span style={{ fontFamily: "'Manrope', sans-serif" }}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Contact/Footer Section
const ContactSection = () => {
  return (
    <section className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="text-center">
          {/* Reference Videos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Reference: Greiner Event
              </h3>
              <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/0o9B-dW8x3k" 
                  title="Greiner Event"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"

                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
            
            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Reference: KBP International (350 os)
              </h3>
              <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/Z9URBj-DJSE" 
                  title="KBP International Event"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Why Work With Us Section */}
          <div className="mt-24">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="font-serif text-4xl md:text-5xl text-slate-100 mb-12 tracking-tight text-center"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Proč organizovat event s námi?
              </h2>
              
              <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
                <p 
                  className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <strong className="text-white">Nejsme pouze produkční agentura.</strong> Jsme realizační společnost s vlastním technickým i cateringovým zázemím a týmem zkušených profesionálů – od kuchařů a techniků až po produkční a logistiku.
                </p>
                
                <p 
                  className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Díky vlastnímu vybavení a internímu týmu máme <strong className="text-white">celý event plně pod kontrolou</strong>. Nejsme závislí na externích dodavatelích, což nám umožňuje garantovat kvalitu, přesné časování i transparentní rozpočet bez zbytečných provizí.
                </p>
                
                <p 
                  className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Klient tak získává partnera, který má realizaci skutečně ve vlastních rukou – <strong className="text-white">od prvního návrhu až po poslední detail akce</strong>.
                </p>
                
                {/* Personal Guarantee */}
                <div className="mt-12 pt-8 border-t border-white/10">
                  <p 
                    className="text-xl md:text-2xl text-emerald-400 font-semibold mb-8 text-center italic"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Za kvalitu i průběh realizace osobně ručím.
                  </p>
                  
                  <div className="flex flex-col items-center gap-1">
                    <img 
                      src="/podpis-vodarek-final.png" 
                      alt="Podpis Jakuba Vodárka" 
                      className="h-24 md:h-32 object-contain"
                    />
                    <div className="text-center">
                      <p 
                        className="text-lg text-white font-semibold"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        Jakub Vodárek
                      </p>
                      <p 
                        className="text-slate-400"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        Jednatel společnosti
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Composition Section */}
          <div className="mt-24">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="font-serif text-4xl md:text-5xl text-slate-100 mb-12 tracking-tight text-center"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Složení teamu pro tento event
              </h2>
              
              <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Main Coordinators */}
                  <div className="space-y-4">
                    <h3 
                      className="text-xl font-bold text-emerald-400 mb-4"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Hlavní koordinátoři
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-semibold">Jakub Vodárek</p>
                          <a href="tel:774750456" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                            774 750 456
                          </a>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-semibold">Monika Ondruchová</p>
                          <a href="tel:773550880" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                            773 550 880
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Technical Team */}
                  <div className="space-y-4">
                    <h3 
                      className="text-xl font-bold text-emerald-400 mb-4"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Technický tým
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-slate-400 text-sm">Pódiový technik / Osvětlovač</p>
                        <p className="text-white font-semibold">David Mrázek</p>
                        <a href="tel:725309731" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm">
                          725 309 731
                        </a>
                      </div>
                      
                      <div>
                        <p className="text-slate-400 text-sm">Hlavní zvukař</p>
                        <p className="text-white font-semibold">Stanislav Valášek</p>
                        <a href="tel:603265426" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm">
                          603 265 426
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Catering Team */}
                  <div className="space-y-4">
                    <h3 
                      className="text-xl font-bold text-emerald-400 mb-4"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Catering tým
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-slate-400 text-sm">Catering</p>
                        <p className="text-white font-semibold">Michal Janota</p>
                        <a href="tel:776760860" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm">
                          776 760 860
                        </a>
                      </div>
                      
                      <div>
                        <p className="text-slate-400 text-sm">Nápojový servis</p>
                        <p className="text-white font-semibold">Vítězslav Remeš</p>
                        <a href="tel:737187828" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm">
                          737 187 828
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* General Contact */}
                <div className="mt-8 pt-8 border-t border-white/10 text-center">
                  <p className="text-slate-400 text-sm mb-2">Obecný kontaktní email</p>
                  <a 
                    href="mailto:info@vseproakci.cz" 
                    className="text-xl font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    info@vseproakci.cz
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Grand Total Section with PDF Export
const GrandTotalSection = ({ guestCount, serviceType, selectedHeadliner, selectedSupportBand, selectedVenue }) => {
  // Pricing constants
  const BASE_PRICE_PER_PERSON = 1200;
  const PREMIUM_SERVICE_EXTRA = 100;
  const VENUE_COST_VSETIN = 91380;
  const VENUE_COST_FRYSTAK = 116471;
  const VENUE_COST_LUKOV = 25000;
  const TECH_COST = 103000;
  const INVENTORY_COST = 27410;
  const DECORATION_COST = 46180; // 34180 + 12000 (Personál - dekorování, instalace světýlek, vázání mašlí, příprava prostor)
  const MODERATOR_COST = 20000;
  const PHOTOBOOTH_COST = 15000;
  const PRODUCTION_COST = 80000;
  const PERSONNEL_COST = 35000;
  
  // Calculate actual personnel cost (subtract 11730 for Vsetín as they provide security and cloakroom)
  const personnelCost = selectedVenue === 'vsetin' ? PERSONNEL_COST - 11730 : PERSONNEL_COST;
  
  // Calculate venue cost
  const venueCost = selectedVenue === 'frystak' 
    ? VENUE_COST_FRYSTAK 
    : selectedVenue === 'lukov' 
      ? VENUE_COST_LUKOV 
      : VENUE_COST_VSETIN;
  const venueName = selectedVenue === 'frystak' 
    ? 'KD Fryšták' 
    : selectedVenue === 'lukov' 
      ? 'KD LUKOV' 
      : 'KD Vsetín';
  
  // Degresivní sleva: 300 osob = 0%, 500+ osob = 10%
  // Sleva se aplikuje POUZE na základní cenu (1200 Kč), ne na příplatek za sklo/porcelán
  const calculateDiscount = (guests) => {
    if (guests <= 300) return 0;
    if (guests >= 500) return 0.10;
    // Lineární interpolace mezi 300-500 osob
    return ((guests - 300) / (500 - 300)) * 0.10;
  };
  
  const discountPercent = calculateDiscount(guestCount);
  
  // Calculate catering cost with degressive discount
  const originalBaseTotalPrice = guestCount * BASE_PRICE_PER_PERSON; // Původní cena bez slevy
  const discountAmount = originalBaseTotalPrice * discountPercent; // Sleva pouze z základní ceny
  const premiumTotalExtra = serviceType === 'premium' ? guestCount * PREMIUM_SERVICE_EXTRA : 0;
  const finalCateringPrice = (originalBaseTotalPrice - discountAmount) + premiumTotalExtra;
  
  // Calculate bands cost
  const headlinerCost = selectedHeadliner 
    ? selectedHeadliner.price + selectedHeadliner.accommodation 
    : 0;
  const supportCost = selectedSupportBand 
    ? selectedSupportBand.price + selectedSupportBand.accommodation 
    : 0;
  const totalBandsCost = headlinerCost + supportCost;
  
  // Calculate program cost (moderator + photobooth + bands)
  const totalProgramCost = MODERATOR_COST + PHOTOBOOTH_COST + totalBandsCost;
  
  // Grand total
  const grandTotal = venueCost + finalCateringPrice + totalProgramCost + TECH_COST + INVENTORY_COST + DECORATION_COST + PRODUCTION_COST + personnelCost;
  
  // PDF Export function
  const handleExportPDF = () => {
    try {
      // pdfMake VFS is auto-configured by the import 'pdfmake/build/vfs_fonts'

    const docDefinition = {
      content: [
        { text: 'Nabídka - Greiner Assistec Vánoční Večírek', style: 'header' },
        { text: `${venueName} • ${selectedVenue === 'frystak' ? '20.11.2026' : selectedVenue === 'lukov' ? '4.12.2026' : '4.12.2026'}`, style: 'subheader', margin: [0, 0, 0, 10] },
        
        // Supplier info
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    { text: 'Dodavatel:', fontSize: 10, bold: true, color: '#1e293b' },
                    { text: 'Jakub Vodárek', fontSize: 11, margin: [0, 3, 0, 2] },
                    { text: 'IČO: 74953613', fontSize: 9, color: '#64748b' },
                    { text: 'DIČ: CZ8805254557', fontSize: 9, color: '#64748b' },
                    { text: 'SNP 1187, Otrokovice, 765 02', fontSize: 9, color: '#64748b' },
                  ],
                  margin: [5, 5, 5, 5]
                }
              ]
            ]
          },
          layout: {
            fillColor: '#f1f5f9',
            hLineWidth: () => 0,
            vLineWidth: () => 0
          },
          margin: [0, 0, 0, 20]
        },
        
        { text: 'Pronájem místa', style: 'sectionHeader' },
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              ['Místo konání:', venueName],
              ['Cena pronájmu:', `${venueCost.toLocaleString('cs-CZ')} Kč${selectedVenue === 'frystak' ? ' + energie' : ''}`],
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20]
        },
        
        { text: 'Catering & Servis', style: 'sectionHeader' },
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              ['Počet hostů:', guestCount.toString()],
              ['Typ servisu:', serviceType === 'premium' ? 'Sklo / Porcelán' : 'Eko servis'],
              ...(discountPercent > 0 ? [
                ['Základní cena bez slevy:', `${originalBaseTotalPrice.toLocaleString('cs-CZ')} Kč`],
                [{ text: `Sleva ${(discountPercent * 100).toFixed(0)}%:`, color: '#059669' }, { text: `-${discountAmount.toLocaleString('cs-CZ')} Kč`, color: '#059669' }],
                ...(serviceType === 'premium' ? [['Příplatek za Sklo/Porcelán:', `+${premiumTotalExtra.toLocaleString('cs-CZ')} Kč`]] : []),
              ] : [
                ...(serviceType === 'premium' ? [['Příplatek za Sklo/Porcelán:', `+${premiumTotalExtra.toLocaleString('cs-CZ')} Kč`]] : []),
              ]),
              [{ text: 'Celková cena cateringu:', bold: true }, { text: `${finalCateringPrice.toLocaleString('cs-CZ')} Kč`, bold: true }],
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20]
        },
        
        { text: 'Program', style: 'sectionHeader' },
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              ['Moderátor & DJ (Ondřej Albrecht):', `${MODERATOR_COST.toLocaleString('cs-CZ')} Kč`],
              ['Fotkoutek (Green screen + rekvizity):', `${PHOTOBOOTH_COST.toLocaleString('cs-CZ')} Kč`],
              ...(selectedHeadliner ? [
                ['Headliner:', selectedHeadliner.name],
                ['  - Honorář:', `${selectedHeadliner.price.toLocaleString('cs-CZ')} Kč`],
                ['  - Ubytování:', `${selectedHeadliner.accommodation.toLocaleString('cs-CZ')} Kč`],
              ] : []),
              ...(selectedSupportBand ? [
                ['Doprovodná kapela:', selectedSupportBand.name],
                ['  - Honorář:', `${selectedSupportBand.price.toLocaleString('cs-CZ')} Kč`],
              ] : []),
              [{ text: 'Celkem za program:', bold: true }, { text: `${totalProgramCost.toLocaleString('cs-CZ')} Kč`, bold: true }],
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20]
        },
        
        { text: 'Ostatní náklady', style: 'sectionHeader' },
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              ['Technické zajištění:', `${TECH_COST.toLocaleString('cs-CZ')} Kč`],
              ['Pronájem inventáře:', `${INVENTORY_COST.toLocaleString('cs-CZ')} Kč`],
              ['Dekorace stolů a prostoru:', `${DECORATION_COST.toLocaleString('cs-CZ')} Kč`],
              ['Produkční / agenturní činnost:', `${PRODUCTION_COST.toLocaleString('cs-CZ')} Kč`],
              ['Personál (koordinace, hostesky vč. identifikačních náramků, šatnáři, úklid, security):', `${personnelCost.toLocaleString('cs-CZ')} Kč`],
              ...(selectedVenue === 'vsetin' ? [[{ text: '  * Security a šatnářky poskytuje v ceně nájmu KD Vsetín (-11 730 Kč)', fontSize: 8, italics: true, color: '#666666', colSpan: 2 }, {}]] : []),
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 30]
        },
        
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              [
                { text: 'CELKOVÁ CENA AKCE (bez DPH)', fontSize: 16, bold: true, color: '#10b981' },
                { text: `${grandTotal.toLocaleString('cs-CZ')} Kč`, fontSize: 18, bold: true, color: '#10b981' }
              ]
            ]
          },
          layout: 'noBorders',
          fillColor: '#f0fdf4',
        },
        
        {
          text: 'Ceny jsou uvedeny bez DPH',
          fontSize: 10,
          color: '#6b7280',
          italics: true,
          margin: [0, 5, 0, 0]
        },
      ],
      styles: {
        header: {
          fontSize: 22,
          bold: true,
          margin: [0, 0, 0, 10]
        },
        subheader: {
          fontSize: 14,
          italics: true,
          color: '#64748b'
        },
        sectionHeader: {
          fontSize: 16,
          bold: true,
          margin: [0, 10, 0, 10],
          color: '#1e293b'
        }
      }
    };

    pdfMake.createPdf(docDefinition).download('Greiner-Assistec-Nabidka.pdf');
    } catch (error) {
      console.error('Chyba při generování PDF:', error);
      alert('Omlouváme se, nepodařilo se vygenerovat PDF. Zkuste to prosím znovu.');
    }
  };

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-[#020617] to-slate-900">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="bg-gradient-to-br from-emerald-900/20 to-slate-900/60 backdrop-blur-xl border-2 border-emerald-400/30 rounded-3xl p-10 shadow-2xl">
          <h2 
            className="font-serif text-4xl md:text-5xl text-center text-white mb-12 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Celkový rozpočet akce
          </h2>
          
          {/* Breakdown */}
          <div className="space-y-4 mb-8">
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <div>
                <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Pronájem místa
                </span>
                <p className="text-sm text-slate-400 mt-1">
                  {venueName}{selectedVenue === 'frystak' && ' + energie'}
                </p>
              </div>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {venueCost.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="py-4 border-b border-white/10">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Catering & Servis
                  </span>
                  <p className="text-sm text-slate-400 mt-1">
                    {guestCount} hostů • {serviceType === 'premium' ? 'Premium servis' : 'Eko servis'}
                  </p>
                </div>
                <span className="text-2xl font-mono font-bold text-emerald-400">
                  {finalCateringPrice.toLocaleString('cs-CZ')} Kč
                </span>
              </div>
              {discountPercent > 0 && (
                <div className="mt-3 pl-4 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Základní cena ({guestCount} × 1 200 Kč)</span>
                    <span className="text-slate-300">{originalBaseTotalPrice.toLocaleString('cs-CZ')} Kč</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400">Sleva {(discountPercent * 100).toFixed(0)}%</span>
                    <span className="text-emerald-400">-{Math.round(discountAmount).toLocaleString('cs-CZ')} Kč</span>
                  </div>
                  {serviceType === 'premium' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Příplatek za Sklo/Porcelán ({guestCount} × 100 Kč)</span>
                      <span className="text-slate-300">+{premiumTotalExtra.toLocaleString('cs-CZ')} Kč</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <div>
                <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Program
                </span>
                <p className="text-sm text-slate-400 mt-1">
                  Ondřej Albrecht • Fotkoutek
                  {selectedHeadliner && ` • ${selectedHeadliner.name}`}
                  {selectedSupportBand && ` • ${selectedSupportBand.name}`}
                </p>
              </div>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {totalProgramCost.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Technické zajištění
              </span>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {TECH_COST.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Pronájem inventáře
              </span>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {INVENTORY_COST.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Dekorace stolů a prostoru
              </span>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {DECORATION_COST.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Produkční / agenturní činnost
              </span>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {PRODUCTION_COST.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
            
            <div className="flex justify-between items-center py-4 border-b border-white/10">
              <div>
                <span className="text-xl text-white font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Personál
                </span>
                <p className="text-sm text-slate-400 mt-1">
                  Koordinace, hostesky vč. identifikačních náramků, šatnáři, úklid, security u vstupu
                </p>
                {selectedVenue === 'vsetin' && (
                  <p className="text-xs text-emerald-400 mt-2 italic">
                    * Security a šatnářky poskytuje KD Vsetín v ceně nájmu (-11 730 Kč)
                  </p>
                )}
              </div>
              <span className="text-2xl font-mono font-bold text-emerald-400">
                {personnelCost.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
          </div>
          
          {/* Grand Total */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-8 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <span 
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  CELKOVÁ CENA
                </span>
                <p className="text-white/90 text-sm mt-2">
                  Ceny jsou uvedeny bez DPH
                </p>
              </div>
              <span 
                className="text-5xl font-bold text-white"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {grandTotal.toLocaleString('cs-CZ')} Kč
              </span>
            </div>
          </div>
          
          {/* PDF Export Button */}
          <button
            onClick={handleExportPDF}
            className="w-full bg-white hover:bg-emerald-50 text-emerald-600 font-bold py-4 px-8 rounded-xl transition-colors flex items-center justify-center gap-3 text-lg mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Stáhnout nabídku (PDF)
          </button>

          {/* Insurance PDF Button */}
          <a
            href="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/6cx2jdqz_Voda%CC%81rek%20Jakub%20-%20Potvrzeni%CC%81%20o%20pojis%CC%8Cte%CC%8Cni%CC%81_8603397856_TREND%2014.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-xl transition-colors flex items-center justify-center gap-3 text-lg mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Pojištění odpovědnosti (PDF)
          </a>

          {/* VOP Button */}
          <a
            href="https://vseproakci.cz/vop/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-slate-700 hover:bg-slate-600 text-white font-bold py-4 px-8 rounded-xl transition-colors flex items-center justify-center gap-3 text-lg"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            VOP pro eventy
          </a>
        </div>
      </div>
    </section>
  );
};


export default GreinerOfferPage;
